import { useState, useEffect, useMemo } from 'react';
import {
  Popover,
  Radio,
  Cascader,
  Select,
  Button,
  Typography,
  Space,
  Divider,
  Tag,
  theme,
} from 'antd';
import type { RadioChangeEvent } from 'antd';
import { useRequest } from 'ahooks';
import { useCustomerTranslation } from '@/hooks/useCustomerTranslation';
import { categoryApi } from '@/services/api';
import { Status } from '@/models/common';
import { locationApi } from '@/services/api';
import { radiusTokens } from '@/styles/tokens';

const { Text } = Typography;

// Cascader option type
interface CascaderOption {
  value: string;
  label: string;
  children?: CascaderOption[];
  isLeaf?: boolean;
}

// Reusable empty state component for filters
interface FilterEmptyStateProps {
  isSearching: boolean;
  noDataText: string;
  noSearchResultText: string;
  colorTextTertiary: string;
  fontSizeSM: number;
}

const FilterEmptyState = ({
  isSearching,
  noDataText,
  noSearchResultText,
  colorTextTertiary,
  fontSizeSM,
}: FilterEmptyStateProps) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '120px',
      padding: '16px',
      color: colorTextTertiary,
      fontSize: fontSizeSM,
    }}
  >
    {isSearching ? noSearchResultText : noDataText}
  </div>
);

// Cascader value type for multiple selection (array of paths)
type CascaderValueType = string[][];

interface CustomerFilterPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (filters: FilterValues) => void;
  initialFilters: FilterValues;
  children: React.ReactElement;
}

export interface FilterValues {
  categories: string[];
  country?: string; // Format: "CountryID-StateID-CityID,CountryID-StateID,CountryID"
  status?: Status;
}

const CustomerFilterPopover = ({
  open,
  onOpenChange,
  onApply,
  initialFilters,
  children,
}: CustomerFilterPopoverProps) => {
  const { token } = theme.useToken();
  const { t, i18n } = useCustomerTranslation();
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    initialFilters.categories || []
  );
  const [selectedCountries, setSelectedCountries] = useState<CascaderValueType>([]);
  // Status filter: 'active' | 'inactive' | 'all'
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>(
    initialFilters.status === 'active' ? 'active' :
    initialFilters.status === 'inactive' ? 'inactive' : 'all'
  );
  const [categorySearchValue, setCategorySearchValue] = useState<string>('');
  const [countrySearchValue, setCountrySearchValue] = useState<string>('');

  // Fetch categories from API (all active categories)
  const { data: categoryData } = useRequest(() => categoryApi.list({ isActive: 1, pageSize: -1 }));

  // Fetch all locations from API with hierarchical structure
  const { data: locationsData, loading: isLoadingLocations } = useRequest(
    () =>
      locationApi.getAllLocations({
        lang: i18n.language as 'en' | 'zh',
        typeId: 2, // Customer type
      }),
    {
      cacheKey: `/api/locations/all?lang=${i18n.language}&typeId=2`,
      staleTime: 30 * 60 * 1000, // 30 minutes
      refreshOnWindowFocus: false,
    }
  );

  // Build cascader options from hierarchical location data
  const cascaderOptions: CascaderOption[] = useMemo(() => {
    if (!locationsData || !locationsData.data || locationsData.data.length === 0) return [];

    // Build Country → State → City hierarchy using countryId (not countryCode)
    return locationsData.data.map((country) => ({
      value: country.countryId.toString(),
      label: country.countryName,
      children: country.states.map((state) => ({
        value: `${country.countryId}-${state.stateId}`,
        label: state.stateName,
        children: state.cities.map((city) => ({
          value: `${country.countryId}-${state.stateId}-${city.cityId}`,
          label: city.cityName,
          isLeaf: true,
        })),
      })),
    }));
  }, [locationsData]);

  // Convert categories to options format
  const categoryOptions = (categoryData?.data || []).map((cat) => ({
    label: cat.category,
    value: cat.categoryId.toString(),
    color: cat.color,
  }));

  // Parse locations string to cascader format
  const parseLocationsString = (locationsStr: string): CascaderValueType => {
    if (!locationsStr || !locationsStr.trim()) return [];

    return locationsStr.split(',').map((location) => {
      const parts = location.trim().split('-');
      if (parts.length === 1) {
        return [parts[0]];
      } else if (parts.length === 2) {
        return [parts[0], `${parts[0]}-${parts[1]}`];
      } else {
        return [parts[0], `${parts[0]}-${parts[1]}`, `${parts[0]}-${parts[1]}-${parts[2]}`];
      }
    });
  };

  // Reset local state when popover opens with new initial values
  useEffect(() => {
    if (open) {
      setSelectedCategories(initialFilters.categories || []);

      if (initialFilters.country) {
        setSelectedCountries(parseLocationsString(initialFilters.country));
      } else {
        setSelectedCountries([]);
      }

      setStatusFilter(
        initialFilters.status === 'active' ? 'active' :
        initialFilters.status === 'inactive' ? 'inactive' : 'all'
      );

      setCategorySearchValue('');
      setCountrySearchValue('');
    }
  }, [open, initialFilters]);

  const handleResetCategories = () => {
    setSelectedCategories([]);
  };

  const handleResetCountries = () => {
    setSelectedCountries([]);
  };

  const handleResetStatus = () => {
    setStatusFilter('all');
  };

  const handleRemoveCategory = (categoryToRemove: string) => {
    setSelectedCategories(selectedCategories.filter((cat) => cat !== categoryToRemove));
  };

  const getColorBarColor = (color?: string) => {
    return color || token.colorPrimary;
  };

  // Convert hex to rgba with low opacity for light background
  const hexToRgba = (hex: string, alpha: number = 0.1) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const handleApply = () => {
    // Convert radio status to Status value for API
    let status: Status | undefined;
    if (statusFilter === 'active') {
      status = 'active';
    } else if (statusFilter === 'inactive') {
      status = 'inactive';
    }
    // 'all' means no status filter (undefined)

    const countryValue = selectedCountries
      .map((path) => {
        const lastValue = path[path.length - 1];
        return lastValue;
      })
      .join(',');

    onApply({
      categories: selectedCategories,
      country: countryValue || undefined,
      status,
    });
    onOpenChange(false);
  };

  const handleCancel = () => {
    setSelectedCategories(initialFilters.categories || []);

    if (initialFilters.country) {
      setSelectedCountries(parseLocationsString(initialFilters.country));
    } else {
      setSelectedCountries([]);
    }

    setStatusFilter(
      initialFilters.status === 'active' ? 'active' :
      initialFilters.status === 'inactive' ? 'inactive' : 'all'
    );

    setCategorySearchValue('');
    setCountrySearchValue('');

    onOpenChange(false);
  };

  const content = (
    <div style={{ padding: '12px' }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {/* Category Filter Section */}
        <div
          style={{
            padding: '12px',
            backgroundColor: token.colorFillQuaternary,
            borderRadius: radiusTokens.panel,
          }}
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <Text
              strong
              style={{
                fontSize: token.fontSizeSM,
                color: token.colorText,
              }}
            >
              {t('customer.filter.category')}
            </Text>
            <Button
              type="link"
              size="small"
              onClick={handleResetCategories}
              style={{ padding: 0, height: 'auto', fontSize: '12px' }}
            >
              {t('customer.filter.reset')}
            </Button>
          </div>

          {/* Selected categories */}
          <div
            style={{
              marginBottom: '10px',
              padding: '8px',
              backgroundColor: token.colorFillQuaternary,
              borderRadius: radiusTokens.card,
              height: '60px',
              overflowY: 'auto',
            }}
          >
            {selectedCategories.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: token.colorTextTertiary,
                }}
              >
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {t('customer.filter.noCategoriesSelected')}
                </Text>
              </div>
            ) : (
              <Space size={[4, 4]} wrap>
                {selectedCategories.map((cat) => {
                  const option = categoryOptions.find((o) => o.value === cat);
                  const categoryColor = option?.color || token.colorPrimary;
                  return (
                    <Tag
                      key={cat}
                      bordered={false}
                      closable
                      onClose={() => handleRemoveCategory(cat)}
                      style={{
                        margin: 0,
                        padding: '0px 4px',
                        fontSize: '11px',
                        lineHeight: '18px',
                        borderRadius: radiusTokens.control,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        backgroundColor: hexToRgba(categoryColor, 0.1),
                        color: categoryColor,
                      }}
                    >
                      {option?.label}
                    </Tag>
                  );
                })}
              </Space>
            )}
          </div>

          {/* Category Select */}
          <style>
            {`
              .customer-category-select-compact .ant-select-selection-overflow-item:not(.ant-select-selection-overflow-item-suffix) {
                display: none !important;
              }
              .customer-category-select-compact .ant-select-selection-overflow-item-suffix {
                display: block !important;
              }
              .customer-category-select-compact .ant-select-selector {
                min-height: 36px !important;
                height: 36px !important;
                display: flex !important;
                align-items: center !important;
                padding: 0 8px !important;
              }
              .customer-category-select-compact .ant-select-selection-wrap {
                height: 100% !important;
                display: flex !important;
                align-items: center !important;
              }
              .customer-category-select-compact .ant-select-selection-overflow {
                height: 100% !important;
                display: flex !important;
                align-items: center !important;
              }
              .customer-category-select-compact .ant-select-selection-overflow-item {
                height: 100% !important;
                display: flex !important;
                align-items: center !important;
              }
              .customer-category-select-compact .ant-select-selection-search {
                height: 100% !important;
                display: flex !important;
                align-items: center !important;
              }
              .customer-category-select-compact .ant-select-selection-search-input {
                height: 100% !important;
              }
              .customer-category-select-compact .ant-select-selection-placeholder {
                position: absolute !important;
                top: 0 !important;
                bottom: 0 !important;
                inset-inline-start: 11px !important;
                inset-inline-end: 11px !important;
                transform: none !important;
                display: flex !important;
                align-items: center !important;
                line-height: normal !important;
              }
            `}
          </style>
          <Select
            mode="multiple"
            placeholder={t('customer.filter.selectCategories')}
            value={selectedCategories}
            onChange={setSelectedCategories}
            onSearch={setCategorySearchValue}
            className="customer-category-select-compact"
            classNames={{ popup: { root: 'customer-category-dropdown-elevated' } }}
            listHeight={200}
            style={{
              width: '100%',
              borderRadius: radiusTokens.card,
            }}
            options={categoryOptions.map((opt) => ({
              ...opt,
              label: (
                <div
                  className="tw-flex tw-items-center tw-gap-2"
                  style={{
                    padding: '4px 8px',
                  }}
                >
                  <div
                    style={{
                      width: '3px',
                      height: '12px',
                      backgroundColor: getColorBarColor(opt.color),
                      borderRadius: radiusTokens.control,
                    }}
                  />
                  <Text style={{ fontSize: '12px' }}>{opt.label}</Text>
                </div>
              ),
            }))}
            notFoundContent={
              <FilterEmptyState
                isSearching={!!categorySearchValue}
                noDataText={t('customer.filter.noCategoriesAvailable')}
                noSearchResultText={t('customer.filter.noSearchResult')}
                colorTextTertiary={token.colorTextTertiary}
                fontSizeSM={token.fontSizeSM}
              />
            }
            maxTagCount={0}
            maxTagPlaceholder={() => null}
            removeIcon={null}
            clearIcon={null}
            menuItemSelectedIcon={null}
            allowClear={false}
            variant="outlined"
          />
          <style>
            {`
              .customer-category-dropdown-elevated {
                background-color: ${token.colorBgElevated} !important;
                border: 1px solid ${token.colorBorderSecondary} !important;
                border-radius: 11px !important;
                box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05) !important;
              }
              .customer-category-dropdown-elevated .ant-select-item {
                padding: 0 !important;
                height: 36px !important;
                display: flex !important;
                align-items: center !important;
              }
              .customer-category-dropdown-elevated .ant-select-item .ant-select-item-option-content {
                height: 36px !important;
                display: flex !important;
                align-items: center !important;
              }
            `}
          </style>
        </div>

        {/* Country Section - Cascader */}
        <div
          style={{
            padding: '12px',
            backgroundColor: token.colorFillQuaternary,
            borderRadius: radiusTokens.panel,
          }}
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <Text
              strong
              style={{
                fontSize: token.fontSizeSM,
                color: token.colorText,
              }}
            >
              {t('customer.filter.location')}
            </Text>
            <Button
              type="link"
              size="small"
              onClick={handleResetCountries}
              style={{ padding: 0, height: 'auto', fontSize: '12px' }}
            >
              {t('customer.filter.reset')}
            </Button>
          </div>

          <Cascader<CascaderOption>
            value={selectedCountries}
            options={cascaderOptions}
            onChange={(value) => {
              setSelectedCountries(value);
            }}
            multiple
            style={{ width: '100%', borderRadius: radiusTokens.card }}
            placeholder={
              isLoadingLocations
                ? t('customer.filter.loadingLocations')
                : t('customer.filter.searchOrSelectLocation')
            }
            disabled={isLoadingLocations}
            expandTrigger="hover"
            showCheckedStrategy="SHOW_PARENT"
            changeOnSelect
            allowClear={false}
            displayRender={(labels) => labels.join(' > ')}
            notFoundContent={
              <FilterEmptyState
                isSearching={!!countrySearchValue}
                noDataText={t('customer.filter.noLocationsAvailable')}
                noSearchResultText={t('customer.filter.noSearchResult')}
                colorTextTertiary={token.colorTextTertiary}
                fontSizeSM={token.fontSizeSM}
              />
            }
            onSearch={setCountrySearchValue}
            showSearch={{
              filter: (inputValue, path) => {
                return path.some((option) =>
                  option.label.toLowerCase().includes(inputValue.toLowerCase())
                );
              },
              render: (_inputValue, path) => {
                return path.map((opt) => opt.label).join(' > ');
              },
              limit: 100,
              matchInputWidth: true,
            }}
            popupRender={(menus) => (
              <div className="customer-cascader-compact-spacing">
                <style>
                  {`
                    .customer-cascader-compact-spacing {
                      border-radius: 11px !important;
                      background-color: ${token.colorBgElevated} !important;
                      border: 1px solid ${token.colorBorderSecondary} !important;
                      box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05) !important;
                    }
                    .customer-cascader-compact-spacing .ant-cascader-menu {
                      border-radius: 11px !important;
                      background-color: ${token.colorBgElevated} !important;
                    }
                    .customer-cascader-compact-spacing .ant-cascader-menu-item {
                      height: 36px !important;
                      padding: 0 8px !important;
                      line-height: 36px !important;
                      font-size: 12px !important;
                      display: flex !important;
                      align-items: center !important;
                    }
                    .customer-cascader-compact-spacing .ant-cascader-checkbox {
                      margin-right: 10px !important;
                      margin-left: 2px !important;
                    }
                    .customer-cascader-compact-spacing .ant-cascader-checkbox-inner {
                      border-radius: 2px !important;
                      width: 16px !important;
                      height: 16px !important;
                      border-width: 1px !important;
                      border-style: solid !important;
                      transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1) !important;
                    }
                    .customer-cascader-compact-spacing .ant-cascader-checkbox:not(.ant-cascader-checkbox-checked):not(.ant-cascader-checkbox-indeterminate) .ant-cascader-checkbox-inner {
                      background-color: ${token.colorBgContainer} !important;
                      border-color: ${token.colorBorder} !important;
                    }
                    .customer-cascader-compact-spacing .ant-cascader-checkbox-checked .ant-cascader-checkbox-inner {
                      background-color: ${token.colorPrimary} !important;
                      border-color: ${token.colorPrimary} !important;
                    }
                    .customer-cascader-compact-spacing .ant-cascader-checkbox-checked .ant-cascader-checkbox-inner::after {
                      position: absolute !important;
                      top: 50% !important;
                      inset-inline-start: 21.5% !important;
                      display: table !important;
                      width: 5.71428571px !important;
                      height: 9.14285714px !important;
                      border: 2px solid #fff !important;
                      border-top: 0 !important;
                      border-inline-start: 0 !important;
                      transform: rotate(45deg) scale(1) translate(-50%, -50%) !important;
                      opacity: 1 !important;
                      content: ' ' !important;
                    }
                    .customer-cascader-compact-spacing .ant-cascader-checkbox-indeterminate .ant-cascader-checkbox-inner {
                      background-color: ${token.colorPrimary} !important;
                      border-color: ${token.colorPrimary} !important;
                    }
                    .customer-cascader-compact-spacing .ant-cascader-checkbox-indeterminate .ant-cascader-checkbox-inner::after {
                      position: absolute !important;
                      top: 50% !important;
                      left: 50% !important;
                      width: 8px !important;
                      height: 8px !important;
                      background-color: #fff !important;
                      border: 0 !important;
                      transform: translate(-50%, -50%) scale(1) !important;
                      opacity: 1 !important;
                      content: ' ' !important;
                    }
                    .customer-cascader-compact-spacing .ant-cascader-checkbox:hover .ant-cascader-checkbox-inner {
                      border-color: ${token.colorPrimary} !important;
                    }
                  `}
                </style>
                {menus}
              </div>
            )}
          />
          <style>
            {`
              .ant-cascader .ant-select-selector {
                border-radius: 8px !important;
                min-height: 36px !important;
                max-height: 60px !important;
                height: auto !important;
                display: flex !important;
                align-items: center !important;
                padding: 5px 8px 1px 8px !important;
                overflow-y: auto !important;
              }
              .ant-cascader .ant-select-selection-overflow {
                display: flex !important;
                align-items: center !important;
                flex-wrap: wrap !important;
              }
              .ant-cascader .ant-select-selection-overflow-item {
                margin-right: 4px !important;
                margin-bottom: 4px !important;
                display: flex !important;
                align-items: center !important;
              }
              .ant-cascader .ant-select-selection-item {
                height: auto !important;
                line-height: 18px !important;
                padding: 0px 4px !important;
                font-size: 11px !important;
                white-space: normal !important;
                word-break: break-word !important;
                max-width: 100% !important;
                background-color: ${token.colorPrimaryBg} !important;
                border: 1px solid ${token.colorPrimaryBorder} !important;
                border-radius: ${radiusTokens.control}px !important;
                color: ${token.colorPrimary} !important;
                margin: 0 !important;
              }
              .ant-cascader .ant-select-selection-item-content {
                white-space: normal !important;
                word-break: break-word !important;
                overflow: visible !important;
                text-overflow: unset !important;
              }
              .ant-cascader .ant-select-selection-item-remove {
                color: ${token.colorPrimary} !important;
                font-size: 10px !important;
                margin-left: 2px !important;
              }
              .ant-cascader .ant-select-selection-item-remove:hover {
                color: ${token.colorPrimaryHover} !important;
              }
              .ant-cascader .ant-select-selection-search {
                height: 100% !important;
                display: flex !important;
                align-items: center !important;
              }
              .ant-cascader .ant-select-selection-search-input {
                height: 100% !important;
              }
              .ant-cascader .ant-select-selection-placeholder {
                position: absolute !important;
                top: 0 !important;
                bottom: 0 !important;
                inset-inline-start: 11px !important;
                inset-inline-end: 11px !important;
                transform: none !important;
                display: flex !important;
                align-items: center !important;
                line-height: normal !important;
                font-size: 12px !important;
              }
            `}
          </style>
        </div>

        {/* Status Filter - Radio Button Cards */}
        <div
          style={{
            padding: '12px',
            backgroundColor: token.colorFillQuaternary,
            borderRadius: radiusTokens.panel,
          }}
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <Text
              strong
              style={{
                fontSize: token.fontSizeSM,
                color: token.colorText,
              }}
            >
              {t('customer.filter.status')}
            </Text>
            <Button
              type="link"
              size="small"
              onClick={handleResetStatus}
              style={{ padding: 0, height: 'auto', fontSize: '12px' }}
            >
              {t('customer.filter.reset')}
            </Button>
          </div>
          <style>
            {`
              .customer-status-card-filter {
                flex: 1;
                padding: 10px 12px;
                cursor: pointer;
                transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .customer-status-card-filter:hover {
                background-color: ${token.colorPrimaryBg} !important;
                border-color: ${token.colorPrimary} !important;
                transform: translateY(-1px);
              }
              .customer-status-card-filter .ant-radio-wrapper {
                margin-right: 0;
              }
            `}
          </style>
          <Radio.Group
            value={statusFilter}
            onChange={(e: RadioChangeEvent) => setStatusFilter(e.target.value)}
            style={{ width: '100%' }}
          >
            <div className="tw-flex tw-gap-2">
              {([
                { value: 'active', label: t('customer.status.active') },
                { value: 'inactive', label: t('customer.status.inactive') },
                { value: 'all', label: t('customer.status.all') },
              ] as const).map((option) => (
                <div
                  key={option.value}
                  className="customer-status-card-filter"
                  style={{
                    backgroundColor: statusFilter === option.value
                      ? token.colorPrimaryBg
                      : token.colorBgContainer,
                    borderRadius: radiusTokens.card,
                    border: `1.5px solid ${
                      statusFilter === option.value ? token.colorPrimary : token.colorBorder
                    }`,
                  }}
                  onClick={() => setStatusFilter(option.value)}
                >
                  <Radio value={option.value}>
                    <Text strong style={{ fontSize: '13px' }}>
                      {option.label}
                    </Text>
                  </Radio>
                </div>
              ))}
            </div>
          </Radio.Group>
        </div>

        <Divider style={{ margin: 0 }} />

        {/* Action Buttons */}
        <div className="tw-flex tw-gap-2 tw-justify-end">
          <Button onClick={handleCancel} style={{ height: '36px', fontSize: token.fontSizeSM }}>
            {t('customer.filter.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleApply}
            style={{ height: '36px', fontSize: token.fontSizeSM }}
          >
            {t('customer.filter.apply')}
          </Button>
        </div>
      </Space>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={onOpenChange}
      placement="bottomLeft"
      arrow={false}
      overlayStyle={{
        width: '400px',
      }}
      styles={{
        body: {
          padding: 0,
          backgroundColor: token.colorBgElevated,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: radiusTokens.panel,
          boxShadow:
            '0 12px 28px 0 rgba(0, 0, 0, 0.12), 0 6px 12px -6px rgba(0, 0, 0, 0.16), 0 18px 56px 16px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      {children}
    </Popover>
  );
};

export default CustomerFilterPopover;
